import { Timestamp } from 'firebase/firestore';
import { OrderStatus } from '../order';
import { CartType, StoreOrderInCart, TaxType } from '../cart';
import { PaymentType } from '../common';

/**
 * 企業團購状态
 */
export enum EnterpriseGroupBuyStatus {
  /** 進行中 */
  ONGOING = 'ONGOING',

  /** 待付款 */
  UNPAID = 'UNPAID',

  /**
   * 已收单
   */
  RECEIVED = 'RECEIVED',

  /**
   * 待取餐
   */
  WAITING = 'WAITING',

  /**
   * 已取餐
   */
  PICKED = 'PICKED',

  /**
   * 取消
   */
  CANCELED = 'CANCELED',
}

export const EnterpriseGroupBuyStatusString: Record<EnterpriseGroupBuyStatus, string> = {
  [EnterpriseGroupBuyStatus.ONGOING]: '進行中',
  [EnterpriseGroupBuyStatus.UNPAID]: '待付款',
  [EnterpriseGroupBuyStatus.RECEIVED]: '已收單',
  [EnterpriseGroupBuyStatus.WAITING]: '待取餐',
  [EnterpriseGroupBuyStatus.PICKED]: '已取餐',
  [EnterpriseGroupBuyStatus.CANCELED]: '取消',
};

/**
 * 企業團購支付状态
 */
export enum EnterpriseGroupBuyPaymentStatus {
  /**
   * 待結單
   */
  UNPAID = 'UNPAID',

  /**
   * 待付款
   */
  WAITING = 'WAITING',

  /**
   * 已付款
   */
  PAID = 'PAID',

  /**
   * 消费者付款
   */
  CONSUMER_PAID = 'CONSUMER_PAID',
}

export const paymentStatusOptions = [
  {
    value: EnterpriseGroupBuyPaymentStatus.UNPAID,
    label: '待結單',
  },
  {
    value: EnterpriseGroupBuyPaymentStatus.WAITING,
    label: '待付款',
  },
  {
    value: EnterpriseGroupBuyPaymentStatus.PAID,
    label: '已付款',
  },
  {
    value: EnterpriseGroupBuyPaymentStatus.CONSUMER_PAID,
    label: '消費者付款',
  },
];

export enum EnterpriseGroupBuyCancelRefundType {
  /**
   * 取消团购并退款
   */
  REFUND = 'REFUND',

  /**
   * 取消团购不退款
   */
  NO_REFUND = 'NO_REFUND',
}

export const EnterpriseGroupBuyCancelRefundTypeString: Record<
  EnterpriseGroupBuyCancelRefundType,
  string
> = {
  [EnterpriseGroupBuyCancelRefundType.REFUND]: '取消團購並退款',
  [EnterpriseGroupBuyCancelRefundType.NO_REFUND]: '取消團購不退款',
};

export enum EnterpriseGroupBuyPaymentType {
  /**
   * 企业统一付款 - 先收款再出餐
   */
  ENTERPRISE_FIRST_PAYMENT = 'ENTERPRISE_FIRST_PAYMENT',

  /**
   * 企业统一付款 - 先出餐再收款
   */
  ENTERPRISE_LAST_PAYMENT = 'ENTERPRISE_LAST_PAYMENT',

  /**
   * 消费者付款
   */
  CONSUMER = 'CONSUMER',
}

export const PaymentTypeString = {
  [EnterpriseGroupBuyPaymentType.ENTERPRISE_FIRST_PAYMENT]: '統一付款',
  [EnterpriseGroupBuyPaymentType.ENTERPRISE_LAST_PAYMENT]: '統一付款',
  [EnterpriseGroupBuyPaymentType.CONSUMER]: '各自付款',
};

export enum EnterpriseGroupBuyPaymentConsumerSubType {
  /**
   * LINE PAY
   */
  LINEPAY = 'LINEPAY',

  /**
   * 信用卡
   */
  CREDIT = 'CREDIT',

  /**
   * 慈济钱包
   */
  TZ_WALLET = 'TZ_WALLET',
}

export enum EnterpriseGroupBuyPaymentEnterpriseSubType {
  /**
   * 平台支付
   */
  PLATFORM = 'PLATFORM',

  /**
   * 企业支付
   */
  ENTERPRISE = 'ENTERPRISE',
}

export const EnterpriseGroupBuyPaymentSubTypeString: Record<
  EnterpriseGroupBuyPaymentConsumerSubType | EnterpriseGroupBuyPaymentEnterpriseSubType,
  string
> = {
  [EnterpriseGroupBuyPaymentConsumerSubType.CREDIT]: '信用卡',
  [EnterpriseGroupBuyPaymentConsumerSubType.LINEPAY]: 'LINE PAY',
  [EnterpriseGroupBuyPaymentConsumerSubType.TZ_WALLET]: '慈濟錢包',
  [EnterpriseGroupBuyPaymentEnterpriseSubType.PLATFORM]: '平台支付',
  [EnterpriseGroupBuyPaymentEnterpriseSubType.ENTERPRISE]: '企業支付',
};

/**
 * 平台信息
 */
export type PlatformInEnterpriseGroupBuy = {
  id: string;
  name: string;
  location: Location;
  currency: string;
  containerExpiredAfterDays: Record<string, number>;
  peopleInCharge?: string;
};

/**
 * 企業團購
 */
export type EnterpriseGroupBuy = {
  /**
   * 平台
   */
  platform: PlatformInEnterpriseGroupBuy;

  /**
   * 企業團購名稱
   */
  name: string;

  /**
   * 企業團購编号（三位英文大写字母）
   */
  miniCode: string;

  /**
   * 企業團購编号（企業團購编号 + MMDDYY + 2位流水号）
   */
  code: string;

  /**
   * 企業取餐地址说明
   */
  addressSpec: string;

  /**
   * 企業取餐地址
   */
  address: string;

  /**
   * 團購收單時間（表单设定）
   */
  orderEndAt: Timestamp;

  /**
   * 團購取餐開始時間（表单设定）
   */
  pickMealAt: Timestamp;

  /**
   * 團購取餐截止時間（表单设定）
   */
  pickMealEnd: Timestamp;

  /**
   * 主辦人姓名
   */
  sponsorName: string;

  /**
   * 主辦人电话
   */
  sponsorPhone: string;

  /**
   * 主辦人的话
   */
  sponsorTalk: string | null;

  /**
   * 联络人姓名
   */
  contaceName: string;

  /**
   * 联络人电话
   */
  contacePhone: string;

  /**
   * 企業團購状态
   */
  status: EnterpriseGroupBuyStatus;

  /**
   * 企業團購狀態記錄時間
   */
  statusRecord: Partial<Record<EnterpriseGroupBuyStatus, Timestamp>>;

  /**
   * 企業支付状态
   */
  paymentStatus: EnterpriseGroupBuyPaymentStatus;

  /**
   * 企業支付狀態記錄時間
   */
  paymentStatusRecord: Partial<Record<EnterpriseGroupBuyPaymentStatus, Timestamp>>;

  /**
   * 企業支付状态
   */
  paymentType: EnterpriseGroupBuyPaymentType;

  /**
   * 支付子方式
   */
  paymentSubType: Array<
    EnterpriseGroupBuyPaymentEnterpriseSubType | EnterpriseGroupBuyPaymentConsumerSubType
  >;

  /**
   * 付款信息
   */
  paymentMeta?: {
    paymentType: EnterpriseGroupBuyPaymentEnterpriseSubType;

    /**
     * 金流序號
     */
    paymentNo: string;

    /**
     * 付款備註
     */
    paymentRemark?: string;
  };

  /**
   * 是否需要統編
   */
  invoice: {
    enable: boolean;

    /**
     * 發票抬頭
     */
    invoiceTitle?: string;

    /**
     * 發票統編
     */
    invoiceTaxId?: string;
  };

  /**
   * 訂單金額
   */
  amount: number;

  /**
   * 分組展示訂單金額
   */
  amountGroup: Record<OrderStatus, number>;

  /**
   * 订单总数
   */
  count: number;

  /**
   * 分组显示订单总数
   */
  countGroup: Record<OrderStatus, number>;

  /**
   * 創建時間
   */
  createdAt: Timestamp;

  /**
   * 已收单时间
   */
  receivedAt: Timestamp | null;

  /**
   * 待取餐时间
   */
  waitingAt: Timestamp | null;

  /**
   * 已取餐时间
   */
  pickedAt: Timestamp | null;

  /**
   * 取消时间
   */
  canceledAt: Timestamp | null;

  /**
   * 取消退款设置
   */
  cancelRefund?: {
    /**
     * 是否允许取消
     */
    type: EnterpriseGroupBuyCancelRefundType;

    /**
     * 退款原因
     */
    reason: string[]; // xxx，其他xxxx
  };

  /**
   * 關聯的商店數量
   */
  storesCount: number;

  /**
   * 製定關聯的品相數量
   */
  storeChooseItemCount: number;

  // 关联的商店信息。
  stores: Array<EnterpriseGroupBuyStore>;

  // 购物车信息。
  orders: number[];
} & { id: string };

/** 企業團購商品关联信息 */
export type EnterpriseGroupBuyStore = {
  isAll: boolean;
  index: number;
  itemIds: string[];
  id: string;
};

export type EnterPriseGroupBuyOrder = {
  cartType: CartType.CART_PERSONAL;
  cartId: `enterprise_group_buy_${string}_${string}`;
  uid: string;
  createdAt: Date;
  customer: {
    email?: string;
    name: string;
    phoneNumber: string;
    uid: string;
  };
  orders: Array<StoreOrderInCart>;
  pickMealAt: Date;
  pickMealEnd: Date;
  platform: PlatformInEnterpriseGroupBuy;
  isGroupOrder: false;
  enterpriseGroupBuyId: string;
  paymentStatusRecord: Partial<Record<EnterpriseGroupBuyPaymentStatus, Timestamp>>;
  paymentInfo: Record<string, any>;
  paymentType: EnterpriseGroupBuyPaymentType;
  discount: number;
  subtotal: number;
  tax: number;
  total: number;
  timezone: string;
  taxInfo?: {
    invoice: string;
    title: string;
  };
  taxType?: TaxType;
  returnContainer: Array<any>;
};

export type EnterpriseGroupBuyCart = {
  cartId: `enterprise_group_buy_${string}_${string}`;
  enterpriseGroupBuyId: string;
  uid: string;
  cartType: CartType.CART_PERSONAL;
  orders: Array<StoreOrderInCart>;
  paymentType: PaymentType;
  pickMealAt: Date;
  pickMealEnd: Date;
  taxInfo?: {
    invoice: string;
    title: string;
  };
  taxType: TaxType;
  coupon: null;
  campaignId: '';
};
